import { BarChart3, CalendarClock, Clock, CreditCard, ImageOff, Loader2, Pause, Play, PlusCircle, RefreshCw, Square, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { dataBR, km, reais, rotaNovoAnuncio, rotaRelatorioDaCampanha, urlImagem, urlRecarga } from "../api";
import AnunciosDoCartao from "./AnunciosDoCartao";
import { Selo } from "./tons";

const retornoFmt = (r) => (r == null ? "—" : `${r.toLocaleString("pt-BR", { maximumFractionDigits: r < 10 ? 2 : 1 })}x`);

function Numero({ rotulo, valor, e2e }) {
  return (
    <div className="rounded-lg bg-muted/60 px-3 py-2 min-w-0" data-e2e={e2e}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{rotulo}</p>
      <p className="text-base font-bold tabular-nums truncate">{valor}</p>
    </div>
  );
}

function Acao({ icone: Icone, children, perigo, ...props }) {
  return (
    <button type="button" {...props}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${perigo ? "border-red-500/40 text-red-500 hover:bg-red-500/10" : "hover:border-primary"}`}>
      <Icone className="w-3.5 h-3.5" />{children}
    </button>
  );
}

function periodoTexto(c) {
  if (c.situacao.codigo === "encerrada") return `de ${dataBR(c.inicio)} a ${dataBR(c.encerrada_em || c.fim)}`;
  if (c.continua) return `contínua · renova em ${dataBR(c.proxima_renovacao_em)}`;
  return `${c.semanas} ${c.semanas === 1 ? "semana" : "semanas"} · até ${dataBR(c.fim)}`;
}

/**
 * Uma campanha criada pelo Núcleo: situação em português, o que foi
 * contratado, os números desde o começo e o que dá para fazer agora.
 * As ações vêm do servidor (`situacao.acoes`) — a tela só as desenha.
 */
export default function CartaoCampanha({ campanha: c, conexao, podeGerir, ocupada, acoes, lojaTemHorario, onValor, onPeriodo, onHorario, onEncerrar }) {
  const navigate = useNavigate();
  const id = c.meta_campaign_id;
  const pode = (acao) => podeGerir && c.situacao.acoes.includes(acao);
  const horario = c.horario_modo === "funcionamento" ? "com a loja aberta" : "o dia todo";
  // Rodando o dia todo numa loja que tem horário: o anúncio aparece com a loja fechada. Fica à vista, com o atalho.
  const avisoHorario = c.horario_modo !== "funcionamento" && lojaTemHorario && pode("horario");

  return (
    <article className="rounded-xl border bg-card p-4 sm:p-5 space-y-3" data-e2e="cartao-campanha" data-situacao={c.situacao.codigo}>
      <div className="flex items-start gap-3">
        {c.imagem
          ? <img src={urlImagem(c.imagem)} alt="" loading="lazy" className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover border bg-muted shrink-0" />
          : <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl border bg-muted flex items-center justify-center shrink-0"><ImageOff className="w-5 h-5 text-muted-foreground" /></span>}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-bold truncate" data-e2e="cartao-titulo">{c.titulo}</h3>
            <Selo situacao={c.situacao} e2e="cartao-situacao" />
            {ocupada && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
          </div>
          {c.situacao.detalhe && <p className={`text-sm mt-0.5 ${c.situacao.tom === "erro" ? "text-red-500" : "text-muted-foreground"}`} data-e2e="cartao-detalhe">{c.situacao.detalhe}</p>}
          {c.situacao.o_que_fazer && <p className="text-sm mt-0.5" data-e2e="cartao-o-que-fazer">{c.situacao.o_que_fazer}</p>}
          <p className="text-xs text-muted-foreground mt-1 tabular-nums">
            <b className="text-foreground">{reais(c.semanal_com_imposto_centavos)}</b> por semana com imposto · {periodoTexto(c)} · raio de {km(c.raio_km)} · <span data-e2e="cartao-horario-texto">{horario}</span>
          </p>
          {avisoHorario && (
            <p className="text-xs text-amber-600 mt-1" data-e2e="cartao-aviso-horario">
              Aparece também com a loja fechada.{" "}
              <button type="button" disabled={ocupada} onClick={() => onHorario(c)} className="underline font-medium hover:text-amber-700">Mudar para só com a loja aberta</button>
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Numero rotulo="Investido" valor={reais(c.investido_centavos)} e2e="cartao-investido" />
        <Numero rotulo="Pedidos" valor={String(c.pedidos)} e2e="cartao-pedidos" />
        <Numero rotulo="Faturamento" valor={reais(c.receita_centavos)} e2e="cartao-faturamento" />
        <Numero rotulo="ROI" valor={retornoFmt(c.retorno)} e2e="cartao-roi" />
      </div>

      <div className="flex flex-wrap gap-2">
        {pode("pagamento") && <a href={urlRecarga(conexao)} target="_blank" rel="noopener noreferrer" data-e2e="cartao-pagamento"
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-sm font-semibold"><CreditCard className="w-3.5 h-3.5" />Resolver pagamento</a>}
        {pode("retomar") && <Acao icone={Play} disabled={ocupada} onClick={() => acoes.retomar(id)} data-e2e="cartao-retomar">Retomar</Acao>}
        {pode("pausar") && <Acao icone={Pause} disabled={ocupada} onClick={() => acoes.pausar(id)} data-e2e="cartao-pausar">Pausar</Acao>}
        {pode("orcamento") && <Acao icone={Wallet} disabled={ocupada} onClick={() => onValor(c)} data-e2e="cartao-valor">Valor por semana</Acao>}
        {pode("periodo") && <Acao icone={c.continua ? RefreshCw : CalendarClock} disabled={ocupada} onClick={() => onPeriodo(c)} data-e2e="cartao-periodo">Período</Acao>}
        {pode("horario") && <Acao icone={Clock} disabled={ocupada} onClick={() => onHorario(c)} data-e2e="cartao-horario">Horário</Acao>}
        {pode("novo_anuncio") && <Acao icone={PlusCircle} disabled={ocupada} onClick={() => navigate(rotaNovoAnuncio(id))} data-e2e="cartao-novo-anuncio">Trocar foto ou texto</Acao>}
        <Acao icone={BarChart3} onClick={() => navigate(rotaRelatorioDaCampanha(id))} data-e2e="cartao-relatorio">Ver relatório</Acao>
        {pode("encerrar") && <Acao icone={Square} perigo disabled={ocupada} onClick={() => onEncerrar(c)} data-e2e="cartao-encerrar">Encerrar</Acao>}
      </div>

      <AnunciosDoCartao campanha={c} podeGerir={podeGerir} ocupada={ocupada} acoes={acoes} />
    </article>
  );
}
